import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { IconGoLive } from 'assets/svg/sprite'
import { AllIcons } from 'helpers/allIcons'
import colors from 'styles/colors'
import screens from 'styles/screens'

interface NewPostPopupProps {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    refButton: React.RefObject<HTMLElement>
}

export function NewPostPopup({
    isOpen,
    setIsOpen,
    refButton
}: NewPostPopupProps) {
    const ref = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const location = useLocation()

    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement

        if (
            ref.current &&
            !ref.current.contains(target) &&
            refButton.current &&
            !refButton.current.contains(target)
        ) {
            setIsOpen(false)
        }
    }

    useEffect(() => {
        if (!isOpen) return

        document.addEventListener('click', handleClickOutside, true)

        return () => {
            document.removeEventListener('click', handleClickOutside, true)
        }
    }, [isOpen])

    return (
        <Container ref={ref} $isOpen={isOpen}>
            <Item to="/new/post/create" onClick={() => setIsOpen(false)}>
                <IconImage
                    src={AllIcons.footer_newpost}
                    alt={t('post')}
                />
                {t('post')}
            </Item>

            <Item
                to={{ pathname: '/messages/new' }}
                state={{ activePage: 'newmessage' }}
                onClick={() => setIsOpen(false)}
            >
                <IconImage
                    src={AllIcons.footer_newmessage}
                    alt={t('message')}
                />
                {t('message')}
            </Item>

            <Item
                to="/new/reel"
                state={{ backgroundPath: location.pathname }}
                onClick={() => setIsOpen(false)}
            >
                <IconImage
                    src={AllIcons.footer_media}
                    alt={t('reel')}
                />
                {t('reel')}
            </Item>

            <Item
                to="/new/story"
                state={{ backgroundPath: location.pathname }}
                onClick={() => setIsOpen(false)}
            >
                <IconImage
                    src={AllIcons.footer_newstory}
                    alt={t('story')}
                />
                {t('story')}
            </Item>

<Item
    to="/live"
    onClick={(e) => {
        e.preventDefault()
    }}
    style={{
        opacity: 0.4,
        cursor: 'not-allowed'
    }}
    className="disabled"
>
    <IconWrapper>
        <IconGoLive />
    </IconWrapper>
    Go Live
</Item>
        </Container>
    )
}

const Container = styled.div<{ $isOpen: boolean }>`
    background: ${colors.black157deg};
    border-radius: 3.5rem;
    padding: 3rem 2rem;

    position: fixed;
    left: auto;
    top: auto;
    transform: translateX(105%);
    z-index: 10;

    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    scrollbar-width: none;

    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};

    &::-webkit-scrollbar {
        width: 0;
        height: 0;
    }

    @media (min-width: ${screens.medium}) {
        background: #f5f5f6;
    }

    @media (max-width: ${screens.medium}) {
        position: absolute;
        transform: translateX(0);

        flex-direction: row;
        align-items: center;

        left: unset;
        top: -110%;
        right: 0;

        max-width: 85vw;
        overflow-x: scroll;

        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        padding: 1.8rem 2.5rem;
    }
`

const Item = styled(Link)`
    display: flex;
    align-items: center;
    white-space: nowrap;

    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-variation-settings: 'wght' 650;
    letter-spacing: 0.02em;
    font-size: 1.3rem;
    line-height: 1.5rem;
    text-decoration: none;
    color: #757576;

    &:hover {
        color: ${colors.white};
    }
    &.disabled,
    &.disabled:hover,
    &.disabled:active,
    &.disabled:focus {
        color: #757576;
    }

    &:last-of-type {
        padding-right: 0;
    }
`

const IconImage = styled.img`
    display: block;
    width: auto;
    height: auto;
    max-width: 23px;
    max-height: 23px;
    margin-right: 1rem;
    flex-shrink: 0;
    object-fit: contain;
    filter: brightness(100);
`

const IconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 23px;
    height: 23px;
    margin-right: 1.2rem;
    flex-shrink: 0;

    svg {
        display: block;
        width: 23px;
        height: 23px;
        max-width: 23px;
        max-height: 23px;

        filter: grayscale(1);
        opacity: 0.6;
    }
`